/** RFC 9457 application/problem+json — the backend's only error format. */
export interface Problem {
  type?: string
  title?: string
  status?: number
  detail?: string
  instance?: string
  /** Field-level validation errors, mapped back onto form fields by name. */
  errors?: { field: string; message: string }[]
}

export class ApiError extends Error {
  readonly status: number
  readonly problem: Problem
  constructor(status: number, problem: Problem) {
    super(problem.detail || problem.title || `Request failed (${status})`)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }
  /** Stable problem `type` slug, e.g. "scope-too-wide". */
  get code(): string {
    const t = this.problem.type ?? ''
    return t.split('/').filter(Boolean).pop() ?? ''
  }
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const e of this.problem.errors ?? []) out[e.field] = e.message
    return out
  }
}

/**
 * Human-facing copy per problem type. A generic 403 on the user screens is
 * genuinely confusing — the backend returns a distinct type per escalation
 * rule so we can say which rule was hit.
 */
export const PROBLEM_COPY: Record<string, string> = {
  'scope-too-wide': "You can't assign a scope broader than your own.",
  'scope-outside-subtree': 'That unit is outside the area you manage.',
  'role-exceeds-grantor': "You can't grant a role that includes permissions you don't have.",
  'last-superuser': 'This is the only account with full access. Assign another first.',
  'password-change-required': 'You need to change your password before continuing.',
  'invalid-credentials': 'Invalid email or password.',
  'account-locked': 'Too many attempts. Try again shortly.',
  'enum-unknown-value': 'This record was written by a newer version. Reload and try again.',
}

export function explain(err: unknown): string {
  if (err instanceof ApiError) return PROBLEM_COPY[err.code] ?? err.message
  if (err instanceof Error) return err.message
  return 'Something went wrong.'
}
